import { Controller } from "@hotwired/stimulus"

/**
 * boolean-toggle-buttons — backs a mutually-exclusive two-button toggle
 * bound to a hidden input that holds "true" / "false". Optionally
 * shows / hides a downstream element by CSS selector.
 *
 * Variants:
 *   "bootstrap" (default) — swaps `btn-primary` ↔ `btn-outline-secondary`.
 *     Used by the public-site waiver form (loads Bootstrap via public.css).
 *   "tailwind"            — swaps `button` ↔ `button-alternative`.
 *     Used by Account/Bullet Train forms whose stylesheet pipeline does
 *     not include Bootstrap; matches the existing `_buttons` field look.
 *
 * Targets:
 *   trueButton, falseButton — the two buttons
 *   input                   — the hidden input that carries the value
 *
 * Values:
 *   revealSelector — CSS selector for an element to show when the value
 *                    is true and hide when false. Optional.
 *   variant        — "bootstrap" or "tailwind". Defaults to "bootstrap".
 */
export default class extends Controller {
  static targets = ["trueButton", "falseButton", "input"]
  static values = {
    revealSelector: String,
    variant: { type: String, default: "bootstrap" }
  }

  connect() {
    this.applyReveal()
  }

  selectTrue(event) {
    event.preventDefault()
    this.setValue(true)
  }

  selectFalse(event) {
    event.preventDefault()
    this.setValue(false)
  }

  setValue(truthy) {
    this.inputTarget.value = truthy ? "true" : "false"
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this._setActive(this.trueButtonTarget, truthy)
    this._setActive(this.falseButtonTarget, !truthy)
    this.applyReveal()
  }

  applyReveal() {
    if (!this.revealSelectorValue) return
    const target = document.querySelector(this.revealSelectorValue)
    if (!target) return
    const truthy = this.inputTarget.value === "true"
    target.style.display = truthy ? "" : "none"
  }

  _setActive(button, active) {
    const { active: activeCls, inactive: inactiveCls } = this._classes()
    if (active) {
      button.classList.add(...activeCls)
      button.classList.remove(...inactiveCls)
    } else {
      button.classList.add(...inactiveCls)
      button.classList.remove(...activeCls)
    }
    button.setAttribute("aria-pressed", String(active))
  }

  _classes() {
    if (this.variantValue === "tailwind") {
      return { active: ["button"], inactive: ["button-alternative"] }
    }
    return { active: ["btn-primary"], inactive: ["btn-outline-secondary"] }
  }
}
